import React, { useState, useEffect } from 'react';
import { Button } from '~/components/Button';
import { EditorInput } from '~/components/Controls/Editor';
import { BreadcrumbsResourcePage } from '~/components/ResourcePage';
import { VerticalTabs, VerticalTabsLayout } from '~/components/VerticalTabs';
import { ResourceLoader, controllableUseResource, useResource } from '~/lib/loader';
import { useAbility } from '~/lib/ability';
import { useFocusOnMount } from '~/lib/useFocusOnMount';
import { AppAdsAbility } from '~/modules/AppAds/ability';
import { fetchSellerDomains, fetchDistributionLinesByDomain, fetchDistributionLinesByPublisher, updateAppAdsLastDownload } from '~/modules/AppAds/api';
import { AppAdsNavigation } from '~/modules/AppAds/components/AppAdsNavigation';
import { ILine } from '~/modules/Clients/models/Line';
import { downloadTxt } from '~/lib/file';
import { Intent } from '~/lib/intent';
import style from './index.module.scss';

export const SellerAppAdsView = () => {
  const ability = useAbility(AppAdsAbility);
  const sellerId = ability.sellerId();

  const [activeDomain, setActiveDomain] = useState<string | undefined>(undefined);
  const contentRef = useFocusOnMount<HTMLDivElement>();

  const domainsResult = useResource(() => {
    if (sellerId) {
      return fetchSellerDomains(sellerId);
    }
    return Promise.resolve([]);
  }, [sellerId]);

  const linesResult = controllableUseResource<ILine[]>(() => {
    if (!sellerId) return Promise.resolve([]);

    if (activeDomain) {
      return fetchDistributionLinesByDomain(activeDomain, sellerId);
    } else {
      return fetchDistributionLinesByPublisher(sellerId);
    }
  }, []);

  useEffect(() => {
    if (sellerId) {
      linesResult.fetch();
    }
  }, [activeDomain, sellerId]);

  const buildAppAdsText = (lines: ILine[]) =>
    lines
      .map((l) =>
        [l.domain, l.externalSellerId, l.sellerType, l.tagId]
          .filter(Boolean)
          .join(',')
      )
      .join('\n');

  const handleDownload = async () => {
    let content = '';
    if (linesResult.resource) {
      content = buildAppAdsText(linesResult.resource);
    }

    const filename = activeDomain
      ? `app-ads-${activeDomain}.txt`
      : `app-ads-${sellerId}.txt`;

    downloadTxt(filename, content);

    if (sellerId) {
      await updateAppAdsLastDownload(sellerId);
    }
  };

  return (
    <ResourceLoader result={domainsResult} message="Loading domains">
      {({ resource: domains }) => {
        const domainTabs = [
          { key: 'common', id: undefined, title: 'Common' },
          ...domains.map((domain) => ({
            key: domain,
            id: domain,
            title: domain,
            tooltip: domain,
          })),
        ];

        return (
          <BreadcrumbsResourcePage
            title="Domains"
            toolbar={
              <div className={style.toolbar}>
                <Button
                  responsive
                  intent={Intent.DEFAULT}
                  disabled={!domains?.length}
                  onClick={handleDownload}
                  leftIcon={<i className="fa fa-download" />}
                >
                  Download
                </Button>
              </div>
            }
          >
            <AppAdsNavigation />
            <div className={`layout-content-box ${style.contentFocusable}`} ref={contentRef} tabIndex={-1}>
              {!domains?.length ? (
                <div className={style.placeholderNote}>No domains available</div>
              ) : (
                <VerticalTabsLayout
                  sidebar={<VerticalTabs items={domainTabs} activeId={activeDomain} onChange={setActiveDomain} />}
                >
                  <ResourceLoader
                    result={linesResult}
                    message={activeDomain ? `Loading lines for ${activeDomain}` : 'Loading all lines'}
                  >
                    {({ resource: lines }) => (
                      <EditorInput
                        mode="text"
                        minLines={20}
                        maxLines={50}
                        disabled
                        canFullScreen
                        canCopy={ability.canCopy()}
                        onCopy={async () => {
                          if (sellerId) {
                            await updateAppAdsLastDownload(sellerId);
                          }
                        }}
                        value={lines ? buildAppAdsText(lines) : ''}
                      />
                    )}
                  </ResourceLoader>
                </VerticalTabsLayout>
              )}
            </div>
          </BreadcrumbsResourcePage>
        );
      }}
    </ResourceLoader>
  );
};
